/**
 * QueryEditor — 줄 하나의 조건식 **상세** 편집기. 묶음 카드(서로 「또는」) 안에 조건 줄(서로 「그리고」)을 놓는다
 * (요청서 판 3 §3-1 · 설계 FE-20260912 §W04 「복합식은 묶음의 의미를 그대로」).
 *
 * 빠른 조건(QuickConditions)과 **같은 식**을 고친다. 빠른 조건은 고른 묶음 하나만 고치므로, 그 묶음을 activeGroup 으로 받아
 * 카드 테두리와 머리 글자로 알리고, 카드 머리를 누르면 그 묶음을 고르게 한다(묶음이 둘 이상일 때만 — 하나면 고를 것이 없다).
 *
 * 검사는 저장·검색으로 나눠 보인다(설계 §5 「검색과 저장의 검증 상태를 분리」 — 예전에는 저장 검사 하나로 검색까지 막았다):
 *   - 구조·한도 오류(inspectExpr search 모드에서도 걸림) — 빨강. 검색도 저장도 막힌다
 *   - 모순(store 모드에서만 걸림) — 노랑. 저장만 막힌다. 검색은 되고 서버가 그 묶음을 0건으로 센다(다른 묶음은 그대로)
 *   - 경고(W1~W3, 화면 몫) — 노란 문장. 결과가 같거나 적을 뿐 틀린 식이 아니다
 * 걸린 자리는 색(테두리)만으로 알리지 않는다 — 걸린 줄 아래 한 줄 글자, 띠 문장 앞에 「묶음 A의 조건 2 —」를 붙인다.
 *
 * 묶음·조건의 React key 는 **식 밖의 평행 배열**로 든다(감사 §6 입력 정합성). 인덱스 key 였을 때는 앞 조건을 지우면
 * 뒤 조건이 앞 자리의 순번 범위·검색어·문제 번호 입력을 물려받았다. 식 객체에 UI id 를 넣지 않는 것은
 * 초안 비교(sameJson)와 256KB 크기 검사가 정규화 전 원본 모양 그대로 재기 때문이다.
 *
 * 값 입력은 축의 모양대로 갈린다: 개념(이름 검색) · 개념 범위(순번 이상·이하 + 포함|제외 — 화면만의 축, 식에는 개념 항) ·
 * 라벨(이름으로 찾기) · 글자(칩) ·
 * 난이도(1~5 배지 — 문제 목록과 같은 색) · 출처(사람·AI·OCR) · AI 종류 · 참·거짓 · 문제 번호(쉼표).
 *
 * 「조건 비우기(제목 줄로)」는 두지 않는다 — 제목 줄 전환은 나무의 줄 더보기에서 하는 명시 동작이다.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import MathName from './MathName';
import { Plus, X } from 'lucide-react';
import ConceptPicker from '../ProblemHubV2/ConceptPicker';
import { difficultyBadgeClass } from '../ProblemHubV2/problemApi';
import LabelPicker from './LabelPicker';
import { makeNames, unknownLabelText } from './names';
import {
  clampGroup, groupName, removeGroup, withMatch,
} from './quickExpr';
import {
  FIELDS, FIELD_ORDER, MATCH_LABEL, LIMITS, ORIGIN_CHOICES, AI_KIND_NAME, RANGE_FIELD, RANGE_LABEL,
  inspectExpr, describeExpr, emptyExpr, emptyGroup, emptyTerm, changeField, pickableMatches, isRangeTerm,
  expandOrderRangeFor, resolveOrderRange, orderBounds, orderRangeShape, orderRangeText, originChoiceOn, toggleOriginChoice, parsePath,
} from './syllabusQuery';

/** 편집기의 축 고르기 — 「개념 범위」(화면만의 축)를 개념 바로 뒤에 */
const EDITOR_FIELDS = FIELD_ORDER.flatMap((f) => (f === 'concept' ? [f, RANGE_FIELD] : [f]));

const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary';
const selectCls = `rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs focus:border-primary focus:outline-none disabled:bg-gray-50 ${focusRing}`;
const pillCls = (on) => `rounded-full border px-2 py-0.5 text-xs transition-colors disabled:cursor-not-allowed ${focusRing} ${
  on ? 'border-primary bg-primary/10 font-medium text-primary' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
}`;
const addBtnCls = `inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40 ${focusRing}`;
const iconBtnCls = `shrink-0 rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-40 ${focusRing}`;

/** 방식의 한 줄 뜻 — select 에 이름만 있으면 「모두」와 「정확히」가 어떻게 다른지 모른다 */
const MATCH_HINT = {
  any: '값 가운데 하나라도 가진 문제',
  all: '값을 모두 가진 문제',
  exact: '개념이 정확히 이 값들뿐인 문제',
};

// ── 평행 key ──

let keySeq = 0;
const nextKey = (prefix) => { keySeq += 1; return `${prefix}${keySeq}`; };
const groupsOf = (expr) => (Array.isArray(expr?.groups) ? expr.groups : []);
const termsIn = (group) => (Array.isArray(group?.terms) ? group.terms : []);

/**
 * 막 만든 개념 범위 조건 — 아직 범위를 적용하지 않은 「포함」은 식만 보면 빈 개념 조건과 같다(유래·값이 없다).
 * 그래서 key 에 range 표시를 들고 가서 가른다. 범위를 적용하면 유래가 붙어 식만으로도 가려진다(isRangeTerm)
 */
const freshRange = (t) => t?.field === 'concept' && t.match === 'any' && !t.source && !(t.values || []).length;

/** 식 모양대로 새 key 를 세운다 — [{ id, terms: [{ id, field, range }] }] */
function keysFor(expr) {
  return groupsOf(expr).map((g) => ({
    id: nextKey('g'), terms: termsIn(g).map((t) => ({ id: nextKey('t'), field: t?.field, range: isRangeTerm(t) })),
  }));
}

/** key 의 조건 종류가 식과 맞는가 — 식이 범위 조건이면 key 도 범위, key 가 범위면 식이 범위이거나 막 만든 범위 */
const kindMatches = (k, t) => (isRangeTerm(t) ? !!k.range : (!k.range || freshRange(t)));

/**
 * key 가 지금 식과 맞는가 — 묶음 수·묶음별 조건 수·자리별 축이 같아야 한다.
 * 축까지 보는 이유: 되돌리기처럼 개수는 같고 내용이 갈린 식이 오면, 옛 자리의 지역 입력이 다른 조건에 남는다.
 */
function keysMatch(keys, expr) {
  const gs = groupsOf(expr);
  return keys.length === gs.length && gs.every((g, i) => {
    const ts = termsIn(g);
    return keys[i].terms.length === ts.length && ts.every((t, j) => keys[i].terms[j].field === t?.field && kindMatches(keys[i].terms[j], t));
  });
}

// ── 문구 ──

// 검사 문장(syllabusQuery — 서버 규칙 그대로 옮긴 것)은 묶음을 번호로 부른다. 화면은 「묶음 A」로 부르므로 여기서 바꿔 적는다.
// 알파벳 A~J(묶음 한도 10)는 읽으면 모두 모음으로 끝나 조사도 는·와 로 바꾼다.
const PARTICLE = { 은: '는', 과: '와' };
const relabelGroups = (message) => String(message || '').replace(
  /묶음 (\d+)(은|과)?/g,
  (_, n, p) => `${groupName(Number(n) - 1)}${p ? PARTICLE[p] : ''}`,
);

/**
 * 띠 문장 앞에 자리를 붙인다 — 구조 오류 문장(「난이도 값이 비어 있습니다」)은 어느 묶음·조건인지 말하지 않아,
 * 붙이지 않으면 자리를 알리는 것이 줄의 빨간 테두리(색) 하나뿐이다(명세 공통 규칙 「색만으로 상태를 말하지 않는다」).
 * 이미 「묶음 N」으로 시작하는 문장(빈 묶음·모순)은 그대로 둔다.
 */
function locatedMessage(err) {
  const msg = String(err?.message || '');
  if (err?.group == null || /^묶음 \d/.test(msg)) return relabelGroups(msg);
  const where = err.term != null ? `${groupName(err.group)}의 조건 ${err.term + 1}` : groupName(err.group);
  return `${where} — ${relabelGroups(msg)}`;
}

/** 서버 오류도 로컬 검사와 같은 위치로 표시한다. 전달 여부는 부모가 실패한 식과 현재 초안을 비교해 결정한다. */
function serverIssueOf(error) {
  if (!error?.details) return null;
  const details = error.details;
  if (!['invalid_expr', 'expr_too_large', 'contradiction', 'unknown_values'].includes(details.reason)) return null;
  const parsed = parsePath(details.path);
  const group = Number.isInteger(details.group) ? details.group : parsed.group;
  const terms = Array.isArray(details.terms) ? details.terms.filter(Number.isInteger) : (parsed.term != null ? [parsed.term] : []);
  return { ...details, group, term: parsed.term, terms, message: error.message || '서버에서 조건을 처리하지 못했습니다.' };
}

const sameSet = (a, b) => {
  const x = new Set(a.map(String));
  const y = new Set(b.map(String));
  return x.size === y.size && [...x].every((v) => y.has(v));
};

/**
 * 손으로 고친 개념 값 — 범위 유래(source)는 **값이 그 범위를 지금 사전으로 펼친 것과 같을 때만** 남긴다(설계 §W02 :100).
 * 무조건 떼면 사전이 바뀌어 어긋난 범위를 손으로 맞춰도 유래가 사라지고, 무조건 남기면 「순번 120~122에서 펼침」이 거짓이 된다.
 */
function withCheckedSource(term, ids, dict) {
  const next = { field: term.field, match: term.match, values: ids };
  const src = term.source;
  if (src && Array.isArray(dict) && ids.length && sameSet(expandOrderRangeFor(dict, src.order_from, src.order_to, term.match), ids)) {
    next.source = { ...src };
  }
  return next;
}

/** 글자 조건(라벨 글자·작성자) — 입력하고 Enter 로 하나씩 */
function TextChips({ value, onChange, disabled, max }) {
  const [text, setText] = useState('');
  const add = () => {
    const s = text.trim();
    if (s && !value.includes(s) && value.length < max) onChange([...value, s]);
    setText('');
  };
  return (
    <div className="flex min-h-8 flex-wrap items-center gap-1 rounded-lg border border-gray-200 bg-white p-1.5">
      {value.map((v) => (
        <span key={v} className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
          「{v}」
          {!disabled && (
            <button type="button" onClick={() => onChange(value.filter((x) => x !== v))} aria-label={`${v} 빼기`} className={`rounded-full text-gray-400 hover:text-gray-600 ${focusRing}`}>
              <X className="size-3" />
            </button>
          )}
        </span>
      ))}
      {!disabled && value.length < max && (
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); add(); } }}
          onBlur={add}
          aria-label="글자 추가"
          placeholder={value.length ? '' : '글자 입력 후 Enter'}
          maxLength={100}
          className="min-w-24 flex-1 border-0 bg-transparent px-1 py-0.5 text-xs focus:outline-none"
        />
      )}
    </div>
  );
}

/**
 * 개념 — 이름 검색으로 고른다. 순번 범위는 따로 된 「개념 범위」 조건이 맡는다(오너 2026-09-15 — 이 칸의 「범위로 채우기」는 걷었다).
 * 유래(source)가 붙은 옛 「모두」·「정확히」 조건은 그 범위를 보이고, 지금 사전과 어긋나면 알린다(요청서 R3 — 대조 알림은 화면 몫).
 */
function ConceptValues({ term, onChange, disabled, dict, conceptName }) {
  const bounds = useMemo(() => orderBounds(dict), [dict]);
  const drift = useMemo(() => {
    if (!term.source || !dict) return false;
    return !sameSet(expandOrderRangeFor(dict, term.source.order_from, term.source.order_to, term.match), term.values);
  }, [term.source, term.values, term.match, dict]);

  return (
    <div className="space-y-1.5">
      {/* 검색 목록은 흐름 안에 — 띄우면 뒤따르는 칸 밑에 깔린다(전역 preserve-3d 로 z 가 먹지 않는다, 부품 주석의 실측).
          밖을 누르면 닫힌다(부품 기본, 오너 2026-09-15). 고른 개념이 없으면 검색 칸을 강조한다 — 여기서 고른다(같은 날) */}
      <ConceptPicker
        value={term.values}
        onChange={(ids) => onChange(withCheckedSource(term, ids, dict))}
        disabled={disabled}
        names={conceptName}
        listInFlow
        highlight={!disabled && term.values.length === 0}
      />
      {term.source && (
        <p className={`text-[11px] ${drift ? 'text-amber-600' : 'text-gray-400'}`}>
          {orderRangeText(term.source, bounds)}에서 펼침
          {drift && ' — 개념 순번이 바뀌어 지금 사전과 맞지 않습니다'}
        </p>
      )}
    </div>
  );
}

/**
 * 개념 범위 — 매스컨셉 조건과 따로 둔 조건. 매스컨셉 순번으로 범위를 잡는다(오너 2026-09-15 「제외 기능은 범위 기능으로 별도로」).
 * 식에는 개념 항으로 들어간다 — 포함은 「하나라도」, 제외는 「제외」(none). 값은 그 범위를 지금 사전으로 펼친 개념 번호이고
 * 유래(source)에 범위를 적는다(요청서 FE-20260914 R3 — 서버는 순번을 모른다). 제외는 폐기 개념까지 펼친다(같은 요청서 Q1).
 * 순번은 한쪽만 넣어도 된다 — 시작만이면 「그 순번 이상」, 끝만이면 「그 순번 이하」(빈 끝은 사전의 처음·끝 순번).
 * 칩을 늘어놓지 않는다 — 「순번 100 이상 제외」 하나가 개념 700개를 넘게 싣는다. 범위와 개수 한 줄로 보인다.
 * 포함 · 제외는 이 칸이 아니라 줄 머리의 드롭다운(축 옆, 개념 줄의 방식 자리)에서 고른다 — withRangeMode.
 */
function RangeValues({ term, onChange, disabled, dict }) {
  const bounds = useMemo(() => orderBounds(dict), [dict]);
  const src = term.source?.kind === 'range' ? term.source : null;
  const shape = src ? orderRangeShape(src, bounds) : null;
  // 열린 끝은 비워 보인다 — 「100 이상」이면 끝 칸이 비어 있어야 다시 적용할 때도 그때 사전의 끝까지 간다
  const srcFrom = src && shape !== 'atMost' ? String(src.order_from) : '';
  const srcTo = src && shape !== 'atLeast' ? String(src.order_to) : '';
  const [from, setFrom] = useState(srcFrom);
  const [to, setTo] = useState(srcTo);
  const [note, setNote] = useState(null);
  // 유래가 밖에서 바뀌면(되돌리기·서버 재조회) 입력을 그 범위로 다시 맞춘다 — 옛 숫자가 「이 범위로 적용했다」처럼 남지 않게(감사 §6)
  useEffect(() => {
    setFrom(srcFrom);
    setTo(srcTo);
    setNote(null);
  }, [srcFrom, srcTo]);
  const drift = useMemo(() => (
    !!(src && dict) && !sameSet(expandOrderRangeFor(dict, src.order_from, src.order_to, term.match), term.values)
  ), [src, dict, term.match, term.values]);

  const apply = (match = term.match) => {
    if (from.trim() === '' && to.trim() === '') { setNote('순번을 넣으세요 — 한쪽만 넣으면 그 순번 이상 · 이하입니다.'); return; }
    const r = resolveOrderRange(dict, from, to);
    if (!r) { setNote('순번은 정수로, 시작이 끝보다 크지 않게 넣으세요.'); return; }
    const ids = expandOrderRangeFor(dict, r.from, r.to, match);
    if (!ids.length) { setNote('그 순번 범위에 개념이 없습니다.'); return; }
    if (ids.length > LIMITS.concept_values) { setNote(`개념은 한 조건에 ${LIMITS.concept_values}개까지입니다.`); return; }
    setNote(null);
    onChange({ field: 'concept', match, values: ids, source: { kind: 'range', order_from: r.from, order_to: r.to } });
  };
  const onKey = (e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); apply(); } };
  const inputCls = 'w-16 rounded-lg border border-gray-200 bg-white px-1.5 py-0.5 text-xs focus:border-primary focus:outline-none disabled:bg-gray-50';

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-1 text-xs text-gray-500">
        <span>순번</span>
        <input type="number" value={from} disabled={disabled} onChange={(e) => setFrom(e.target.value)} onKeyDown={onKey} aria-label="순번 시작" placeholder="처음" className={inputCls} />
        <span>이상 ~</span>
        <input type="number" value={to} disabled={disabled} onChange={(e) => setTo(e.target.value)} onKeyDown={onKey} aria-label="순번 끝" placeholder="끝" className={inputCls} />
        <span>이하</span>
        {!disabled && <button type="button" onClick={() => apply()} disabled={!dict} title="한쪽만 넣으면 그 순번 이상 · 이하" className={addBtnCls}>적용</button>}
      </div>
      {term.values.length > 0 && (
        <p className={`text-[11px] ${drift ? 'text-amber-600' : 'text-gray-500'}`}>
          {src ? orderRangeText(src, bounds) : '범위 기록 없음 ·'} 개념 {term.values.length}개 {term.match === 'none' ? '제외' : '포함'}
          {drift && ' — 개념 순번이 바뀌어 지금 사전과 맞지 않습니다. [적용]을 다시 누르면 맞춰집니다'}
        </p>
      )}
      {note && <p className="text-[11px] text-amber-600">{note}</p>}
    </div>
  );
}

/**
 * 개념 범위 조건의 포함 · 제외를 바꾼 조건 — 범위가 있으면 같은 범위를 그 방식대로 다시 펼친다(제외만 폐기 개념까지).
 * 범위가 아직 없으면 방식만 바꾼다(막 만든 조건)
 */
function withRangeMode(term, match, dict) {
  const src = term.source?.kind === 'range' ? term.source : null;
  if (!src) return { ...term, match };
  const ids = expandOrderRangeFor(dict, src.order_from, src.order_to, match);
  return { field: 'concept', match, values: ids.length ? ids : term.values, source: { ...src } };
}

/** 쉼표·빈칸으로 가른 문제 번호 — 숫자가 아닌 조각은 그대로 둬 식 검사가 「형식이 잘못되었습니다」로 짚게 한다 */
const parseProblemIds = (text) => text.split(/[\s,]+/).filter(Boolean).map((s) => (/^\d+$/.test(s) ? Number(s) : s));

/**
 * 문제 번호 — 제어 입력. 글자는 지역 상태로 들고 blur·Enter 에서 식에 반영한다(자판마다 식을 바꾸면 「1,」에서 오류가 깜박인다).
 * 식 쪽 값이 바뀌면(되돌리기·재조회) 글자도 따라간다 — defaultValue 였을 때는 처음 값이 DOM 에 남았다(감사 §6).
 */
function ProblemValues({ term, onChange, disabled }) {
  const joined = term.values.join(', ');
  const [text, setText] = useState(joined);
  useEffect(() => { setText(joined); }, [joined]);
  const apply = () => {
    const values = parseProblemIds(text);
    if (JSON.stringify(values) === JSON.stringify(term.values)) { setText(joined); return; }
    onChange({ ...term, values });
  };
  return (
    <input
      type="text"
      value={text}
      disabled={disabled}
      aria-label="문제 번호"
      placeholder="문제 번호를 쉼표로"
      onChange={(e) => setText(e.target.value)}
      onBlur={apply}
      onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); apply(); } }}
      className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-primary focus:outline-none disabled:bg-gray-50"
    />
  );
}

function ValueEditor({
  term, range = false, onChange, disabled, dict, names, labelNames, onRememberLabels,
}) {
  const spec = FIELDS[term.field];
  const setValues = (values) => onChange({ ...term, values });
  if (!spec) return null;
  if (range) return <RangeValues term={term} onChange={onChange} disabled={disabled} dict={dict} />;
  if (spec.kind === 'concept') return <ConceptValues term={term} onChange={onChange} disabled={disabled} dict={dict} conceptName={names.concept} />;
  if (spec.kind === 'label') {
    // 모르는 라벨은 「라벨 이름 확인 필요 · 앞 8자」— 해석 문장·빠른 조건과 같은 표기로(설계 §W02, names.unknownLabelText)
    const shown = { ...labelNames };
    term.values.forEach((u) => {
      const k = String(u).toLowerCase();
      if (!shown[k]) shown[k] = unknownLabelText(u);
    });
    return <LabelPicker value={term.values} onChange={setValues} names={shown} onRemember={onRememberLabels} disabled={disabled} />;
  }
  if (spec.kind === 'text') {
    return <TextChips value={term.values} onChange={setValues} disabled={disabled} max={LIMITS.text_values} />;
  }
  if (term.field === 'difficulty') {
    return (
      <div className="flex flex-wrap gap-1">
        {[1, 2, 3, 4, 5].map((n) => {
          const on = term.values.includes(n);
          return (
            <button
              key={n}
              type="button"
              disabled={disabled}
              aria-pressed={on}
              aria-label={`난이도 ${n}`}
              onClick={() => setValues(on ? term.values.filter((v) => v !== n) : [...term.values, n].sort((a, b) => a - b))}
              className={`inline-flex size-7 items-center justify-center rounded-full text-xs font-medium transition-colors ${focusRing} ${
                on ? difficultyBadgeClass(n) : 'border border-gray-200 bg-white text-gray-400 hover:bg-gray-50'
              }`}
            >
              {n}
            </button>
          );
        })}
      </div>
    );
  }
  if (term.field === 'origin') {
    return (
      <div className="flex flex-wrap gap-1">
        {ORIGIN_CHOICES.map((c) => (
          <button key={c.key} type="button" disabled={disabled} aria-pressed={originChoiceOn(term.values, c)} title={c.hint} onClick={() => setValues(toggleOriginChoice(term.values, c))} className={pillCls(originChoiceOn(term.values, c))}>
            {c.label}
          </button>
        ))}
      </div>
    );
  }
  if (term.field === 'ai_kind') {
    return (
      <div className="flex flex-wrap gap-1">
        {['generated', 'variation'].map((k) => {
          const on = term.values.includes(k);
          return (
            <button key={k} type="button" disabled={disabled} aria-pressed={on} onClick={() => setValues(on ? term.values.filter((v) => v !== k) : [...term.values, k])} className={pillCls(on)}>
              {AI_KIND_NAME[k]}
            </button>
          );
        })}
      </div>
    );
  }
  if (spec.kind === 'bool') {
    const yes = term.field === 'needs_analysis' ? '필요' : '있음';
    const no = term.field === 'needs_analysis' ? '완비' : '없음';
    return (
      <div className="flex flex-wrap gap-1">
        {[[true, yes], [false, no]].map(([v, label]) => (
          <button key={label} type="button" disabled={disabled} aria-pressed={term.values[0] === v} onClick={() => setValues([v])} className={pillCls(term.values[0] === v)}>
            {label}
          </button>
        ))}
      </div>
    );
  }
  if (term.field === 'problem') return <ProblemValues term={term} onChange={onChange} disabled={disabled} />;
  return null;
}

function QueryEditor({
  value,
  onChange,
  readOnly = false,
  dict = null,
  labelNames = {},
  onRememberLabels = null,
  activeGroup = null, // 빠른 조건이 고치는 묶음 번호(없으면 표시하지 않는다)
  onActiveGroupChange = null, // (g) => void — 카드 머리를 누르면 그 묶음을 고른다
  serverError = null,
}) {
  const [keys, setKeys] = useState(() => keysFor(value));
  let rowKeys = keys;
  if (!keysMatch(keys, value)) {
    // 식의 모양이 밖에서 바뀌었다(빠른 조건·되돌리기·서버 재조회). 어느 줄이 어느 줄이 됐는지 알 수 없으니
    // 전부 새 key 로 세운다 — 옛 자리의 지역 입력이 다른 조건에 남는 것보다 새로 서는 편이 맞다
    rowKeys = keysFor(value);
    setKeys(rowKeys);
  }
  const searchCheck = useMemo(() => (value ? inspectExpr(value, { mode: 'search' }) : null), [value]);
  const storeCheck = useMemo(() => (value ? inspectExpr(value) : null), [value]);
  const names = useMemo(() => makeNames(dict, labelNames), [dict, labelNames]);
  const serverIssue = useMemo(() => serverIssueOf(serverError), [serverError]);

  if (value == null) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 p-3 text-xs text-gray-500">
        조건 없는 제목 줄입니다 — 나무에서 누르면 펼치기만 합니다.
        {!readOnly && (
          <button type="button" onClick={() => onChange(emptyExpr())} className={`${addBtnCls} ml-1`}>
            <Plus className="size-3" /> 조건 만들기
          </button>
        )}
      </div>
    );
  }

  const groups = groupsOf(value);
  const multi = groups.length > 1;
  // 구조·한도 오류가 먼저다 — 그게 있으면 모순 검사는 돌지도 않는다(서버도 같은 순서)
  const blocking = searchCheck?.error || null;
  const saveOnly = blocking ? null : (storeCheck?.error || null);
  const marked = blocking || serverIssue || saveOnly;
  const markedDanger = !!(blocking || serverIssue);
  const termHit = (i, j) => marked != null && marked.group === i && (marked.terms || []).includes(j);
  const groupHit = (i) => marked != null && marked.group === i;
  const active = activeGroup == null ? null : clampGroup(value, activeGroup);
  const canPick = multi && typeof onActiveGroupChange === 'function';

  // 식과 key 를 한 번에 바꾼다 — 같은 이벤트 안이라 부모의 새 식과 새 key 가 한 렌더에 함께 선다
  const commit = (next, nextKeys) => { setKeys(nextKeys); onChange(next); };
  const withGroups = (nextGroups) => ({ ...value, groups: nextGroups });

  const setTerm = (i, j, term) => commit(
    withGroups(groups.map((g, gi) => (gi !== i ? g : { ...g, terms: termsIn(g).map((t, tj) => (tj === j ? term : t)) }))),
    rowKeys.map((k, gi) => (gi !== i ? k : { ...k, terms: k.terms.map((tk, tj) => (tj === j ? { ...tk, field: term.field } : tk)) })),
  );
  // 축 바꾸기 — 「개념 범위」는 화면만의 축이라 식에는 개념 항으로 들어간다. 매스컨셉 ↔ 개념 범위도 값을 비운다(뜻이 다르다)
  const changeAxis = (i, j, t, f) => {
    let term = changeField(t, f);
    let range = false;
    if (f === RANGE_FIELD) { term = { field: 'concept', match: 'any', values: [] }; range = true; } else if (f === 'concept') term = emptyTerm('concept');
    commit(
      withGroups(groups.map((g, gi) => (gi !== i ? g : { ...g, terms: termsIn(g).map((x, tj) => (tj === j ? term : x)) }))),
      rowKeys.map((k, gi) => (gi !== i ? k : { ...k, terms: k.terms.map((tk, tj) => (tj === j ? { ...tk, field: term.field, range } : tk)) })),
    );
  };
  const removeTerm = (i, j) => commit(
    withGroups(groups.map((g, gi) => (gi !== i ? g : { ...g, terms: termsIn(g).filter((_, tj) => tj !== j) }))),
    rowKeys.map((k, gi) => (gi !== i ? k : { ...k, terms: k.terms.filter((_, tj) => tj !== j) })),
  );
  const addTerm = (i) => {
    const term = emptyTerm('concept');
    commit(
      withGroups(groups.map((g, gi) => (gi !== i ? g : { ...g, terms: [...termsIn(g), term] }))),
      rowKeys.map((k, gi) => (gi !== i ? k : { ...k, terms: [...k.terms, { id: nextKey('t'), field: term.field }] })),
    );
  };
  const addGroup = () => {
    const g = emptyGroup();
    commit(withGroups([...groups, g]), [...rowKeys, ...keysFor({ groups: [g] })]);
  };
  // 묶음 지우기는 빠른 조건과 같은 규칙(quickExpr.removeGroup) — 마지막 묶음이면 빈 묶음 하나로 남긴다(미완성 초안).
  // 저절로 제목 줄(null)로 바꾸지 않는다 — 제목 줄은 전체 문제 검색이 되는 다른 뜻이다(설계 §W06)
  const removeGroupAt = (i) => {
    const next = removeGroup(value, i);
    commit(next, multi ? rowKeys.filter((_, gi) => gi !== i) : keysFor(next));
    // 고른 묶음 번호가 지운 묶음 뒤에 있으면 한 칸 당긴다 — 그대로 두면 빠른 조건이 엉뚱한 묶음을 고친다
    if (multi && active != null && typeof onActiveGroupChange === 'function') {
      const na = active > i ? active - 1 : (active === i ? clampGroup(next, i) : active);
      if (na !== active) onActiveGroupChange(na);
    }
  };

  const hasAnyTerm = groups.some((g) => termsIn(g).length > 0);
  // 한도에 닿으면 늘리는 손잡이를 막고 이유를 title 로 단다(설계 §5 「한도 접근·초과 이유를 해당 편집 위치에서」).
  // 전체 조건 수(32)도 본다 — 묶음마다 10개 아래여도 합이 넘으면 누르자마자 한도 오류가 선다. 새 묶음도 조건 하나를 들고 온다
  const totalTerms = groups.reduce((n, g) => n + termsIn(g).length, 0);
  const totalReason = totalTerms >= LIMITS.terms_total ? `조건은 모두 합쳐 ${LIMITS.terms_total}개까지입니다` : null;
  const addTermBlock = (n) => (n >= LIMITS.terms_per_group ? `한 묶음의 조건은 ${LIMITS.terms_per_group}개까지입니다` : totalReason);
  const addGroupBlock = groups.length >= LIMITS.groups ? `묶음은 ${LIMITS.groups}개까지입니다` : totalReason;

  return (
    <div className="space-y-2">
      <p className="rounded-xl bg-gray-50 px-3 py-2 text-xs leading-relaxed text-gray-600">
        <span className="mr-1 font-medium text-gray-700">해석</span>
        {hasAnyTerm ? <MathName text={describeExpr(value, names)} compact /> : '아직 조건이 없습니다'}
      </p>

      {groups.map((g, i) => {
        const gk = rowKeys[i];
        const terms = termsIn(g);
        const isActive = multi && active === i;
        let border = 'border-gray-200';
        if (groupHit(i)) border = markedDanger ? 'border-danger/50' : 'border-amber-300';
        else if (isActive) border = 'border-primary/40';
        const head = (
          <>
            <span className="font-medium text-gray-700">{groupName(i)}</span>
            <span>— 아래 조건을 모두 만족</span>
          </>
        );
        return (
          <Fragment key={gk.id}>
            {i > 0 && <div className="text-center text-[11px] font-medium text-gray-400">또는</div>}
            <section aria-label={groupName(i)} className={`space-y-1.5 rounded-xl border p-2 ${border}`}>
              <div className="flex flex-wrap items-center gap-1 text-[11px] text-gray-500">
                {canPick ? (
                  <button
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => onActiveGroupChange(i)}
                    title={`빠른 조건으로 ${groupName(i)} 고치기`}
                    className={`inline-flex items-center gap-1 rounded-full px-1 py-0.5 transition-colors hover:bg-gray-100 ${focusRing}`}
                  >
                    {head}
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1 px-1 py-0.5">{head}</span>
                )}
                {isActive && (
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">빠른 조건이 고치는 묶음</span>
                )}
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => removeGroupAt(i)}
                    disabled={!multi && terms.length === 0}
                    aria-label={`${groupName(i)} 지우기`}
                    title={multi ? `${groupName(i)} 지우기` : '마지막 묶음이라 지우면 빈 묶음으로 남습니다'}
                    className={`ml-auto ${iconBtnCls}`}
                  >
                    <X className="size-3" />
                  </button>
                )}
              </div>
              {terms.length === 0 && (
                <p className={`px-1 text-xs ${groupHit(i) && blocking ? 'text-red-600' : 'text-gray-400'}`}>조건을 하나 이상 고르세요</p>
              )}
              {/* 묶음 자리의 오류(한 묶음 조건 수 한도 등) — 짚을 조건이 없으니 카드 안에 글자로 */}
              {terms.length > 0 && groupHit(i) && !(marked.terms || []).length && (
                <p className={`px-1 text-[11px] ${markedDanger ? 'text-red-600' : 'text-amber-700'}`}>{relabelGroups(marked.message)}</p>
              )}
              {terms.map((t, j) => {
                const spec = FIELDS[t.field];
                const tk = gk.terms[j];
                const range = isRangeTerm(t) || (!!tk.range && freshRange(t));
                const matches = range ? [] : pickableMatches(t.field);
                let ring = '';
                if (termHit(i, j)) ring = markedDanger ? 'bg-danger/5 ring-1 ring-danger/40' : 'bg-amber-50 ring-1 ring-amber-300';
                return (
                  <Fragment key={tk.id}>
                    {j > 0 && <div className="pl-1 text-[10px] text-gray-400">그리고</div>}
                    <div className={`flex flex-wrap items-start gap-1.5 rounded-lg p-1 ${ring}`}>
                      <select value={range ? RANGE_FIELD : t.field} disabled={readOnly} onChange={(e) => changeAxis(i, j, t, e.target.value)} aria-label="검색 축" className={selectCls}>
                        {EDITOR_FIELDS.map((f) => <option key={f} value={f}>{f === RANGE_FIELD ? RANGE_LABEL : FIELDS[f].label}</option>)}
                      </select>
                      {/* 개념 범위 조건은 방식 자리에 포함 · 제외(오너 2026-09-15 「제외 조건이 드롭다운에 없음」).
                          범위 기록 없이 값만 든 옛 제외는 포함으로 돌리면 매스컨셉 조건으로 바뀌어 버리므로 막는다 */}
                      {range && (
                        <select value={t.match === 'none' ? 'none' : 'any'} disabled={readOnly} onChange={(e) => setTerm(i, j, withRangeMode(t, e.target.value, dict))} aria-label="포함 · 제외" className={selectCls}>
                          <option value="any" disabled={!t.source && (t.values || []).length > 0}>포함</option>
                          <option value="none">제외</option>
                        </select>
                      )}
                      {/* 방식은 매스컨셉 조건의 하나라도·모두·정확히까지 — 「제외」는 개념 범위 조건의 포함 · 제외에만 있다 */}
                      {spec && matches.length > 1 && (
                        <select value={t.match} disabled={readOnly} onChange={(e) => setTerm(i, j, withMatch(t, e.target.value))} aria-label="엮는 방식" title={MATCH_HINT[t.match] || ''} className={selectCls}>
                          {matches.map((m) => <option key={m} value={m} title={MATCH_HINT[m]}>{MATCH_LABEL[m]}</option>)}
                        </select>
                      )}
                      {/* 범위 줄은 값 칸을 넓게 잡는다 — 드롭다운 둘이 개념 줄보다 좁아 값 칸이 첫 줄에 끼면 × 만 홀로 아랫줄로 떨어진다(1440 실측).
                          넓히면 개념 줄처럼 값 칸이 둘째 줄로 내려가고 × 가 그 옆에 선다 */}
                      <div className={`min-w-0 flex-1 ${range ? 'basis-64' : 'basis-48'}`}>
                        {/* 축이 바뀌면 값 입력을 새로 세운다 — 라벨 글자 → 작성자처럼 같은 부품이면 치던 글자가 남는다 */}
                        <ValueEditor
                          key={`${tk.id}:${range ? RANGE_FIELD : t.field}`}
                          term={t}
                          range={range}
                          onChange={(nt) => setTerm(i, j, nt)}
                          disabled={readOnly}
                          dict={dict}
                          names={names}
                          labelNames={labelNames}
                          onRememberLabels={onRememberLabels}
                        />
                      </div>
                      {!readOnly && (
                        <button type="button" onClick={() => removeTerm(i, j)} aria-label={`${groupName(i)}의 조건 ${j + 1} 지우기`} className={iconBtnCls}>
                          <X className="size-3.5" />
                        </button>
                      )}
                      {/* 걸린 줄은 테두리 색에 더해 글자로도 짚는다(색만으로 상태를 말하지 않는다 · 설계 W03 「해당 조건에 이유 표시」) */}
                      {termHit(i, j) && (
                        <p className={`basis-full px-1 text-[11px] ${markedDanger ? 'text-red-600' : 'text-amber-700'}`}>
                          {markedDanger ? relabelGroups(marked.message) : '모순 — 이 조건 때문에 저장할 수 없습니다(검색은 됩니다)'}
                        </p>
                      )}
                    </div>
                  </Fragment>
                );
              })}
              {!readOnly && (
                <button type="button" onClick={() => addTerm(i)} disabled={addTermBlock(terms.length) != null} title={addTermBlock(terms.length) || undefined} className={addBtnCls}>
                  <Plus className="size-3" /> 그리고 조건
                </button>
              )}
            </section>
          </Fragment>
        );
      })}

      {!readOnly && (
        <div className="flex flex-wrap items-center gap-1">
          <button type="button" onClick={addGroup} disabled={addGroupBlock != null} title={addGroupBlock || undefined} className={addBtnCls}>
            <Plus className="size-3" /> 또는 묶음
          </button>
        </div>
      )}

      {blocking && (
        <p role="alert" className="rounded-2xl bg-red-50 p-3 text-xs text-red-600">
          <span className="block">{locatedMessage(blocking)}</span>
          <span className="block">고치기 전에는 검색도 저장도 할 수 없습니다.</span>
        </p>
      )}
      {serverIssue && !blocking && (
        <p role="alert" className="rounded-2xl bg-red-50 p-3 text-xs text-red-600">
          <span className="block">서버 응답 — {locatedMessage(serverIssue)}</span>
          <span className="block">표시된 조건을 확인하세요. 조건을 바꾸면 이전 응답의 위치 표시는 사라집니다.</span>
        </p>
      )}
      {saveOnly && (
        <p role="status" className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          <span className="block">{locatedMessage(saveOnly)}</span>
          <span className="block">저장할 수 없습니다 — 검색은 할 수 있고 이 묶음은 0건으로 셉니다.</span>
        </p>
      )}
      {storeCheck && storeCheck.warnings.length > 0 && (
        <ul className="space-y-0.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          {storeCheck.warnings.map((w, k) => (
            // 표시 전용 목록이라 자리 key 로 충분하다(지역 상태가 없다)
            // eslint-disable-next-line react/no-array-index-key
            <li key={k}>{relabelGroups(w.message)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default QueryEditor;
